import { Champ, ChampPuces, ChampSelection } from "@/composants/ui/champ";
import { optionsCategorie, type OptionCategorie } from "@/composants/formulaire-lot";
import type { Adresse } from "@/lib/api";
import { MODES_REMISE } from "@/lib/libelles";

/**
 * Ce que la commande porte (ADR-0031), saisi une seule fois pour la création
 * comme pour la correction : la catégorie, les deux natures, et ses deux
 * adresses.
 */

export type ValeursAdresse = { ligne1: string; ligne2: string; code_postal: string; ville: string };

export const ADRESSE_VIDE: ValeursAdresse = { ligne1: "", ligne2: "", code_postal: "", ville: "" };

export function depuisAdresse(adresse: Adresse | null): ValeursAdresse {
  return adresse ? { ...adresse, ligne2: adresse.ligne2 ?? "" } : ADRESSE_VIDE;
}

/** Une adresse sans première ligne n'est pas saisie : elle part à `null`. */
export function enAdresse(valeurs: ValeursAdresse): Adresse | null {
  if (!valeurs.ligne1.trim()) return null;
  return { ...valeurs, ligne2: valeurs.ligne2 || null };
}

export type Natures = { mode_remise: "EMPORT" | "LIVRAISON"; avec_pose: boolean };

/** L'adresse de réalisation est facultative à l'emport sans pose seulement. */
export function realisationRequise({ mode_remise, avec_pose }: Natures): boolean {
  return mode_remise === "LIVRAISON" || avec_pose;
}

const OPTIONS_REMISE = Object.entries(MODES_REMISE).map(([valeur, libelle]) => ({
  valeur,
  libelle,
}));

export function libelleNatures({ mode_remise, avec_pose }: Natures): string {
  return `${MODES_REMISE[mode_remise]}, ${avec_pose ? "avec" : "sans"} pose`;
}

export function ChampCategorieCommande({
  categories,
  valeur,
  onChanger,
}: {
  categories: OptionCategorie[];
  valeur: string;
  onChanger: (categorie: string) => void;
}) {
  return (
    <ChampSelection
      libelle="Catégorie"
      id="categorie"
      required
      options={optionsCategorie(categories, valeur)}
      value={valeur}
      onChange={(e) => onChanger(e.target.value)}
    />
  );
}

export function ChampsNatures({
  valeurs,
  onChanger,
}: {
  valeurs: Natures;
  onChanger: (valeurs: Natures) => void;
}) {
  return (
    <>
      <ChampPuces
        libelle="Remise"
        required
        options={OPTIONS_REMISE}
        valeur={valeurs.mode_remise}
        onChanger={(mode) => onChanger({ ...valeurs, mode_remise: mode as Natures["mode_remise"] })}
      />
      <label className="flex items-center gap-2 self-end pb-3 text-sm text-encre">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={valeurs.avec_pose}
          onChange={(e) => onChanger({ ...valeurs, avec_pose: e.target.checked })}
        />
        Avec pose
      </label>
    </>
  );
}

export function ChampsAdresse({
  prefixe,
  valeurs,
  onChanger,
  requise,
}: {
  prefixe: string;
  valeurs: ValeursAdresse;
  onChanger: (valeurs: ValeursAdresse) => void;
  requise: boolean;
}) {
  const poser = (champ: keyof ValeursAdresse) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChanger({ ...valeurs, [champ]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Champ
        libelle="Adresse"
        id={`${prefixe}-ligne1`}
        required={requise}
        className="sm:col-span-2"
        value={valeurs.ligne1}
        onChange={poser("ligne1")}
      />
      <Champ
        libelle="Complément d'adresse"
        id={`${prefixe}-ligne2`}
        className="sm:col-span-2"
        value={valeurs.ligne2}
        onChange={poser("ligne2")}
      />
      <Champ
        libelle="Code postal"
        id={`${prefixe}-cp`}
        required={requise}
        inputMode="numeric"
        value={valeurs.code_postal}
        onChange={poser("code_postal")}
      />
      <Champ
        libelle="Ville"
        id={`${prefixe}-ville`}
        required={requise}
        value={valeurs.ville}
        onChange={poser("ville")}
      />
    </div>
  );
}

/** L'adresse sur une ligne, pour une carte ou une liste. */
export function adresseEnLigne(adresse: Adresse | null): string {
  return adresse ? `${adresse.ligne1}, ${adresse.ville}` : "Emport, sans adresse de réalisation";
}

export function AdresseEnBloc({ adresse }: { adresse: Adresse | null }) {
  if (!adresse) return <>Aucune (emport sans pose)</>;
  return (
    <>
      {adresse.ligne1}
      {adresse.ligne2 ? <br /> : null}
      {adresse.ligne2}
      <br />
      {adresse.code_postal} {adresse.ville}
    </>
  );
}
